import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Plane, CheckCircle, Clock, Shield, MapPin, Package, ClipboardList, FileCheck, CreditCard, PackageCheck, ArrowRight, ChevronRight, Home, DollarSign, Truck } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface UsaToLagosPageProps {
  onNavigate?: (page: string) => void;
}
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12
    }
  }
};
export function UsaToLagosPage({
  onNavigate
}: UsaToLagosPageProps) {
  return <div className="flex flex-col bg-white" data-id="element-2441">
      {/* Hero Section */}
      <section className="relative min-h-[520px] lg:min-h-[600px] text-white overflow-hidden -mt-20 sm:-mt-[104px] pt-20 sm:pt-[104px]" data-id="element-2442">
        <div className="absolute inset-0" data-id="element-2443">
          <img src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Cargo shipping containers" className="absolute inset-0 w-full h-full object-cover" data-id="element-2444" />
          <div className="absolute inset-0 bg-gray-900/65" data-id="element-2445" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/50 to-gray-900/30" data-id="element-2446" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-gray-900/30" data-id="element-2447" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 sm:pt-20 sm:pb-20 lg:pt-28 lg:pb-28" data-id="element-2448">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-8" data-id="element-2449">
            <button onClick={() => onNavigate?.('home')} className="flex items-center gap-1 hover:text-white transition-colors" data-id="element-2450">
              <Home className="w-4 h-4" data-id="element-2451" />
              <span data-id="element-2452">Home</span>
            </button>
            <ChevronRight className="w-4 h-4" data-id="element-2453" />
            <span className="text-white/50" data-id="element-2454">Services</span>
            <ChevronRight className="w-4 h-4" data-id="element-2455" />
            <span className="text-white font-medium" data-id="element-2456">USA to Lagos</span>
          </nav>

          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="max-w-3xl" data-id="element-2457">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-black/40 border border-white/30 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-2458">
              <Plane className="w-4 h-4" data-id="element-2459" />
              Our Core Service
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-tight" style={{
            textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.4)'
          }} data-id="element-2460">
              USA to Lagos, <span className="text-brand-300" data-id="element-2461">Nigeria</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl leading-relaxed" style={{
            textShadow: '0 1px 3px rgba(0,0,0,0.5)'
          }} data-id="element-2462">
              Direct air and sea freight from any US location to Lagos. Weekly
              departures with expedited customs clearing and door-to-door
              delivery options.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-2463">
              <Button size="lg" className="bg-brand-500 hover:bg-brand-600 text-white border-none shadow-lg px-8" onClick={() => onNavigate?.('shipping')} data-id="element-2464">
                Start Shipping Now <ArrowRight className="ml-2 w-5 h-5" data-id="element-2465" />
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 px-8" onClick={() => onNavigate?.('contact')} data-id="element-2466">
                Get a Quote
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-brand-600 py-8" data-id="element-2467">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2468">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-white text-center" data-id="element-2469">
            <div data-id="element-2470">
              <div className="text-3xl font-bold mb-1" data-id="element-2471">7–14</div>
              <div className="text-brand-100 text-sm" data-id="element-2472">Days Delivery</div>
            </div>
            <div data-id="element-2473">
              <div className="text-3xl font-bold mb-1" data-id="element-2474">50K+</div>
              <div className="text-brand-100 text-sm" data-id="element-2475">Packages Delivered</div>
            </div>
            <div data-id="element-2476">
              <div className="text-3xl font-bold mb-1" data-id="element-2477">99.9%</div>
              <div className="text-brand-100 text-sm" data-id="element-2478">Success Rate</div>
            </div>
            <div data-id="element-2479">
              <div className="text-3xl font-bold mb-1" data-id="element-2480">4</div>
              <div className="text-brand-100 text-sm" data-id="element-2481">
                US Drop-off Locations
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 lg:py-24 bg-white" data-id="element-2482">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2483">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-2484">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-2485">
              <span className="inline-block px-4 py-1.5 bg-brand/10 text-brand text-sm font-semibold rounded-full mb-6" data-id="element-2486">
                What's Included
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6" data-id="element-2487">
                Everything You Need, End to End
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-2488">
                Our USA to Lagos service is fully managed — from the moment you
                drop off your package to the moment it arrives at the door in
                Lagos. No surprises, no hidden fees.
              </p>
              <div className="space-y-4" data-id="element-2489">
                <div className="flex items-start gap-3" data-id="element-2490">
                  <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" data-id="element-2491" />
                  <div data-id="element-2492">
                    <div className="font-semibold text-gray-900" data-id="element-2493">
                      Drop-off at any US location
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-2494">
                      New York, Houston, Atlanta, and more
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2495">
                  <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" data-id="element-2496" />
                  <div data-id="element-2497">
                    <div className="font-semibold text-gray-900" data-id="element-2498">
                      Customs clearance included
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-2499">
                      We handle all Nigerian import documentation
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2500">
                  <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" data-id="element-2501" />
                  <div data-id="element-2502">
                    <div className="font-semibold text-gray-900" data-id="element-2503">
                      Real-time tracking
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-2504">
                      Know exactly where your package is at all times
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2505">
                  <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" data-id="element-2506" />
                  <div data-id="element-2507">
                    <div className="font-semibold text-gray-900" data-id="element-2508">
                      Cargo insurance available
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-2509">
                      Protect your valuables with optional coverage
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2510">
                  <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" data-id="element-2511" />
                  <div data-id="element-2512">
                    <div className="font-semibold text-gray-900" data-id="element-2513">
                      Door-to-door in Lagos
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-2514">
                      Delivered directly to your recipient's address
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2515">
                  <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" data-id="element-2516" />
                  <div data-id="element-2517">
                    <div className="font-semibold text-gray-900" data-id="element-2518">
                      Weekly departures
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-2519">
                      Consistent, reliable shipping schedule
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative" data-id="element-2520">
              <div className="rounded-2xl overflow-hidden shadow-2xl" data-id="element-2521">
                <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="Logistics warehouse" className="w-full h-auto" data-id="element-2522" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-4 border border-gray-100" data-id="element-2523">
                <div className="flex items-center gap-3" data-id="element-2524">
                  <div className="w-12 h-12 bg-brand-50 rounded-lg flex items-center justify-center" data-id="element-2525">
                    <MapPin className="w-6 h-6 text-brand-600" data-id="element-2526" />
                  </div>
                  <div data-id="element-2527">
                    <div className="text-lg font-bold text-gray-900" data-id="element-2528">
                      USA → Lagos
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-2529">Direct Route</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 lg:py-24 bg-gray-50" data-id="element-2530">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2531">
          <div className="text-center mb-14" data-id="element-2532">
            <span className="inline-block px-4 py-1.5 bg-brand/10 text-brand text-sm font-semibold rounded-full mb-4" data-id="element-2533">
              Transparent Pricing
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-2534">
              Simple, Honest Rates
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-2535">
              Pricing is based on weight and dimensions. No hidden fees — what
              you see is what you pay.
            </p>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-2536">
            <motion.div variants={fadeIn} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm text-center" data-id="element-2537">
              <div className="w-14 h-14 bg-brand-50 rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-2538">
                <Package className="w-7 h-7 text-brand-600" data-id="element-2539" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2540">
                Small Packages
              </h3>
              <div className="text-3xl font-bold text-brand-600 mb-1" data-id="element-2541">
                $8
                <span className="text-base font-normal text-gray-500" data-id="element-2542">/lb</span>
              </div>
              <p className="text-sm text-gray-500" data-id="element-2543">Up to 10 lbs</p>
            </motion.div>

            <motion.div variants={fadeIn} className="bg-brand-600 rounded-2xl p-6 border border-brand-500 shadow-lg text-center relative" data-id="element-2544">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-400 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-2545">
                Most Popular
              </div>
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-2546">
                <Package className="w-7 h-7 text-white" data-id="element-2547" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-2548">
                Medium Packages
              </h3>
              <div className="text-3xl font-bold text-white mb-1" data-id="element-2549">
                $6
                <span className="text-base font-normal text-brand-200" data-id="element-2550">
                  /lb
                </span>
              </div>
              <p className="text-sm text-brand-200" data-id="element-2551">10–50 lbs</p>
            </motion.div>

            <motion.div variants={fadeIn} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm text-center" data-id="element-2552">
              <div className="w-14 h-14 bg-brand-50 rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-2553">
                <Package className="w-7 h-7 text-brand-600" data-id="element-2554" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2555">
                Large Packages
              </h3>
              <div className="text-3xl font-bold text-brand-600 mb-1" data-id="element-2556">
                $5
                <span className="text-base font-normal text-gray-500" data-id="element-2557">/lb</span>
              </div>
              <p className="text-sm text-gray-500" data-id="element-2558">50+ lbs</p>
            </motion.div>
          </motion.div>

          <div className="text-center mt-8" data-id="element-2559">
            <Button size="lg" onClick={() => onNavigate?.('shipping')} className="bg-brand-500 hover:bg-brand-600 text-white border-none px-10" data-id="element-2560">
              Calculate Your Rate <ArrowRight className="ml-2 w-5 h-5" data-id="element-2561" />
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 lg:py-24 bg-secondary-900 text-white" data-id="element-2562">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2563">
          <div className="text-center mb-16" data-id="element-2564">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-id="element-2565">
              How It Works
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-2566">
              Four simple steps from your door to Lagos.
            </p>
          </div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-2567">
            <motion.div variants={fadeIn} className="flex flex-col items-center text-center" data-id="element-2568">
              <div className="relative w-20 h-20 bg-secondary-800 rounded-2xl border border-secondary-700 flex items-center justify-center mb-5" data-id="element-2569">
                <ClipboardList className="w-9 h-9 text-brand-400" data-id="element-2570" />
                <div className="absolute -top-3 -right-3 w-7 h-7 bg-brand-500 rounded-full flex items-center justify-center text-white font-bold text-sm" data-id="element-2571">
                  1
                </div>
              </div>
              <h3 className="text-lg font-bold mb-2" data-id="element-2572">Fill the Form</h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-2573">
                Enter your shipment details and get an instant quote.
              </p>
            </motion.div>
            <motion.div variants={fadeIn} className="flex flex-col items-center text-center" data-id="element-2574">
              <div className="relative w-20 h-20 bg-secondary-800 rounded-2xl border border-secondary-700 flex items-center justify-center mb-5" data-id="element-2575">
                <FileCheck className="w-9 h-9 text-brand-400" data-id="element-2576" />
                <div className="absolute -top-3 -right-3 w-7 h-7 bg-brand-500 rounded-full flex items-center justify-center text-white font-bold text-sm" data-id="element-2577">
                  2
                </div>
              </div>
              <h3 className="text-lg font-bold mb-2" data-id="element-2578">Review & Confirm</h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-2579">
                Check your summary and confirm all shipment details.
              </p>
            </motion.div>
            <motion.div variants={fadeIn} className="flex flex-col items-center text-center" data-id="element-2580">
              <div className="relative w-20 h-20 bg-secondary-800 rounded-2xl border border-secondary-700 flex items-center justify-center mb-5" data-id="element-2581">
                <CreditCard className="w-9 h-9 text-brand-400" data-id="element-2582" />
                <div className="absolute -top-3 -right-3 w-7 h-7 bg-brand-500 rounded-full flex items-center justify-center text-white font-bold text-sm" data-id="element-2583">
                  3
                </div>
              </div>
              <h3 className="text-lg font-bold mb-2" data-id="element-2584">Pay Securely</h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-2585">
                Complete payment using your preferred method.
              </p>
            </motion.div>
            <motion.div variants={fadeIn} className="flex flex-col items-center text-center" data-id="element-2586">
              <div className="relative w-20 h-20 bg-secondary-800 rounded-2xl border border-secondary-700 flex items-center justify-center mb-5" data-id="element-2587">
                <PackageCheck className="w-9 h-9 text-brand-400" data-id="element-2588" />
                <div className="absolute -top-3 -right-3 w-7 h-7 bg-brand-500 rounded-full flex items-center justify-center text-white font-bold text-sm" data-id="element-2589">
                  4
                </div>
              </div>
              <h3 className="text-lg font-bold mb-2" data-id="element-2590">Track & Receive</h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-2591">
                Monitor your package in real-time until it arrives.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-white" data-id="element-2592">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2593">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-gradient-to-br from-brand-600 to-brand-700 rounded-3xl p-10 sm:p-16 shadow-xl" data-id="element-2594">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-id="element-2595">
              Ready to ship to Lagos?
            </h2>
            <p className="text-xl text-brand-100 mb-8 max-w-xl mx-auto" data-id="element-2596">
              Get started in minutes. Weekly departures, door-to-door delivery.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2597">
              <Button variant="white" size="lg" className="px-10" onClick={() => onNavigate?.('shipping')} data-id="element-2598">
                Start Shipping Now
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 px-10" onClick={() => onNavigate?.('contact')} data-id="element-2599">
                Contact Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}